'use client';

import { cn } from '@/lib/utils';
import { useMemo, useState } from 'react';

type Mode = 'wake-at' | 'sleep-at';

const CYCLE_MINUTES = 90;
const FALL_ASLEEP_MINUTES = 15;
const CYCLE_COUNTS = [6, 5.5, 5, 4.5, 4, 3.5];

function parseTime(value: string) {
  const [h, m] = value.split(':').map(Number);
  const date = new Date();
  date.setHours(h, m, 0, 0);
  return date;
}

function formatTime(date: Date) {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export function SleepCalculator() {
  const [mode, setMode] = useState<Mode>('wake-at');
  const [time, setTime] = useState('07:00');

  const suggestions = useMemo(() => {
    const anchor = parseTime(time);
    return CYCLE_COUNTS.map((cycles) => {
      const totalMinutes = cycles * CYCLE_MINUTES + FALL_ASLEEP_MINUTES;
      const result = new Date(anchor);
      if (mode === 'wake-at') {
        result.setMinutes(result.getMinutes() - totalMinutes);
      } else {
        result.setMinutes(result.getMinutes() + totalMinutes);
      }
      return { cycles, time: result };
    });
  }, [mode, time]);

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex gap-2">
        {(
          [
            { id: 'wake-at', label: 'I want to wake up at…' },
            { id: 'sleep-at', label: "I'm going to bed at…" },
          ] as const
        ).map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setMode(m.id)}
            className={cn(
              'rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors',
              mode === m.id
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            {m.label}
          </button>
        ))}
      </div>

      <label className="mt-5 block">
        <span className="text-xs font-medium text-muted-foreground">
          {mode === 'wake-at' ? 'Wake-up time' : 'Bedtime'}
        </span>
        <input
          type="time"
          name="sleep-time"
          autoComplete="off"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          className="mt-1 h-11 w-full rounded-lg border border-input bg-background px-3 text-lg text-foreground outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
        />
      </label>

      <p className="mt-5 text-xs font-medium text-muted-foreground">
        {mode === 'wake-at'
          ? 'Go to bed at one of these times'
          : 'Try waking up at one of these times'}
      </p>
      <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-6">
        {suggestions.map((s) => (
          <div key={s.cycles} className="rounded-xl bg-primary/10 px-2 py-3 text-center">
            <p className="text-sm font-bold text-primary">{formatTime(s.time)}</p>
            <p className="text-[11px] text-muted-foreground">{s.cycles} cycles</p>
          </div>
        ))}
      </div>
    </div>
  );
}
